// ThisWeekTab — the new home tab (redesign Phase 3a).
//
// Shows the church's current published 5-day devotional (built from last
// Sunday's sermon) front and centre: a sermon row, a gold devotional card
// with M-T-W-T-F progress, a face stack of who's done today's, and the
// church's upcoming events with an "I'm going" toggle.
//
// Phase 3a scope: only the primary "has a church, has a current plan" state.
// The "sermon only" alternate state, the dedicated Sermon Recap screen, and
// the Read/Reflect/Respond devotional-day restyle are all deferred to Phase
// 3b — tapping the sermon row opens the existing FullSermonSheet directly
// for now (its own "Full notes" button will become the Recap screen's entry
// point once that exists).
//
// The "no church yet" state (built in redesign Phase 8a, per
// docs/designs/WeekNoChurch.dc.html) is handled directly in this file: an
// inline find-your-church search (joins straight away, or "Request to join"
// for invite-only churches, reusing the same access_requests flow as
// Onboarding.jsx's find-church step), a static "while you look" nudge into
// the Gospel of John, and a "can't see your church?" card. The mockup's
// second "I'm a church leader" row is intentionally omitted — it points at
// the gated Part B self-serve leader flow, which isn't built.
//
// Props:
//   churchId, userId, profile, isModerator
//   onOpenPlanDay(plan, follow, dayNumber) — open ReadingPlanDaySheet
//   onOpenSermon(sermon)                   — open FullSermonSheet
//   onOpenLeaders()                        — open LeadersPage (redesign Phase 7)
//   onOpenVerse(book, chapter, verse)       — open the Bible tab at a verse
//   onAddEvent()                           — open EventEditorSheet, blank (elders/admins)
//   onEditEvent(event, occurrenceDate)     — open EventEditorSheet for one occurrence (elders/admins)
//   onDeleteEvent(event, occurrenceDate)   — open EventEditorSheet straight at its delete
//                                             confirm/scope-choice flow (elders/admins)
//   onEventsRefresh(refreshFn)             — hands this tab's useEvents() refresh up to
//                                             BibleApp.jsx, so EventEditorSheet (mounted
//                                             there) can refetch after a save/delete
//   onOpenBible(book?, chapter?)           — switch to the Bible tab; with no args, resumes
//                                             wherever the user last was (BibleApp.jsx's own
//                                             restore); with args, jumps to that chapter
//   onOpenCommunity()                      — switch to the Community tab
// (Past-plans browsing lives in the Sermons tab as of Phase 4 — this screen
// no longer needs an "All plans" escape hatch.)

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../hooks/useAuth";
import { useEvents } from "../hooks/useEvents";
import { shareChurchInvite, shareTextViaBestChannel } from "../lib/shareInvite";
import { displayTitleOf } from "../lib/sermonSeries";
import FaceStack from "./FaceStack";
import ReadingPlanReviewSheet from "./ReadingPlanReviewSheet";

const GOLD = "var(--gold)";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

const EVENT_BADGE = { bg: "var(--peach)", ink: "var(--peach-ink)" };

// First-visit devotional-card pulse (redesign Phase 8a) — plays once per
// user, then never again. Scoped per-user in localStorage like the app's
// other readUserScoped-style flags.
function hasSeenFirstDevoCard(userId) {
  if (typeof window === "undefined" || !userId) return true;
  try { return localStorage.getItem(`nlc.sawFirstDevoCard:${userId}`) === "1"; }
  catch { return true; }
}
function markSeenFirstDevoCard(userId) {
  if (typeof window === "undefined" || !userId) return;
  try { localStorage.setItem(`nlc.sawFirstDevoCard:${userId}`, "1"); } catch {}
}

function leaderSummaryText({ reports, plans, access }) {
  const parts = [];
  if (reports > 0) parts.push(`${reports} ${reports === 1 ? "report" : "reports"}`);
  if (plans > 0) parts.push(`${plans} ${plans === 1 ? "plan" : "plans"} to approve`);
  if (access > 0) parts.push(`${access} ${access === 1 ? "person" : "people"} asking to join`);
  return parts.join(" · ");
}

// The sermon's actual main passage, not just whatever ref happens to be
// first — used for both the sermon card's "[passage] · Recap →" line and
// "This week's verse", so the two never disagree (docs/FIX-SERMON-CLIPS.md §3c).
function mainRefOf(sermon) {
  const refs = sermon?.sermon_refs || [];
  return refs.find(r => r.kind === "main_passage") || refs[0] || null;
}
function rangeLabelOf(ref) {
  if (!ref) return "";
  return `${ref.book} ${ref.chapter}:${ref.verse}${ref.end_verse && ref.end_verse !== ref.verse ? `–${ref.end_verse}` : ""}`;
}

function fmtEventBadge(d) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  if (target.getTime() === now.getTime()) return "Today";
  return target.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

// Mirrors BibleApp.jsx's own "nlc.lastPlace" read — a read-only snapshot for
// the "Keep reading" card's label. Falls back to the same Romans 8 default
// BibleApp.jsx uses for first-time visits.
function readLastPlace() {
  if (typeof window === "undefined") return { book: "Romans", chap: 8 };
  try {
    const raw = JSON.parse(localStorage.getItem("nlc.lastPlace") || "{}");
    if (raw.book && raw.chap) return { book: raw.book, chap: raw.chap };
  } catch {}
  return { book: "Romans", chap: 8 };
}

export default function ThisWeekTab({ churchId, churchName, userId, isModerator, onOpenPlanDay, onOpenSermon, onOpenLeaders, onOpenVerse, onAddEvent, onEditEvent, onDeleteEvent, onEventsRefresh, onOpenBible, onOpenCommunity, onSaveVerse }) {
  const { requestAccess, refreshProfile } = useAuth();
  const [plan, setPlan] = useState(null);
  const [follow, setFollow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [totalJoined, setTotalJoined] = useState({ profiles: [], total: 0 });
  const [leaderCounts, setLeaderCounts] = useState({ reports: 0, plans: 0, access: 0 });
  // Add/Edit/Delete on events sit behind the cog, off by default, rather
  // than Edit/Delete always showing on every row.
  const [manageEventsOpen, setManageEventsOpen] = useState(false);
  // "I'm going" failures used to be silent (console.warn only) — this
  // surfaces them right on the row that failed, for a few seconds.
  const [rsvpError, setRsvpError] = useState(null); // { key, message } | null

  async function handleToggleGoing(ev) {
    const key = `${ev.id}-${ev.occurrenceDate}`;
    setRsvpError(null);
    const { error } = await toggleGoing(ev.id, ev.occurrenceDate, ev.going);
    if (error) setRsvpError({ key, message: error });
  }
  const [pulseDevo, setPulseDevo] = useState(false);
  const [standaloneSermon, setStandaloneSermon] = useState(null);

  const { events, error: eventsError, toggleGoing, refresh: refreshEvents } = useEvents(churchId);

  // Hand the refresh fn up to BibleApp.jsx, which mounts EventEditorSheet
  // (shared with church settings) outside this tab.
  useEffect(() => { onEventsRefresh?.(refreshEvents); }, [onEventsRefresh, refreshEvents]);

  // First-visit pulse on the devotional (or sermon, if no devotional yet)
  // card — plays once, then marks itself seen so it never repeats.
  useEffect(() => {
    if (loading || !churchId) return;
    if (hasSeenFirstDevoCard(userId)) return;
    setPulseDevo(true);
    markSeenFirstDevoCard(userId);
  }, [loading, churchId, userId]);

  // "For leaders" summary card counts — cheap count-only queries, safe to
  // run on every This Week visit. The full list views live in LeadersPage.
  useEffect(() => {
    if (!isModerator || !churchId) { setLeaderCounts({ reports: 0, plans: 0, access: 0 }); return; }
    let cancelled = false;
    (async () => {
      const [reportsRes, plansRes, accessRes] = await Promise.all([
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("church_id", churchId).eq("resolved", false),
        supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("church_id", churchId).in("status", ["draft", "approved"]),
        supabase.from("access_requests").select("id", { count: "exact", head: true }).eq("church_id", churchId).eq("status", "pending"),
      ]);
      if (cancelled) return;
      setLeaderCounts({ reports: reportsRes.count || 0, plans: plansRes.count || 0, access: accessRes.count || 0 });
    })();
    return () => { cancelled = true; };
  }, [isModerator, churchId]);

  const load = useCallback(async () => {
    if (!churchId) { setPlan(null); setFollow(null); setLoading(false); return; }
    setLoading(true);
    let { data: planRow, error: planErr } = await supabase
      .from("reading_plans")
      .select("*, sermons(id, title, display_title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)), reading_plan_days(*)")
      .eq("church_id", churchId)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    // Tolerate display_title not existing yet (migration not run) — retry
    // without it rather than silently showing "no plan" for everyone.
    if (planErr && /display_title/i.test(planErr.message || "")) {
      ({ data: planRow } = await supabase
        .from("reading_plans")
        .select("*, sermons(id, title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)), reading_plan_days(*)")
        .eq("church_id", churchId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle());
    }
    if (planRow) {
      planRow.reading_plan_days = (planRow.reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number);
    }
    setPlan(planRow || null);

    let followRow = null;
    if (planRow && userId) {
      const { data } = await supabase
        .from("reading_plan_follows")
        .select("*")
        .eq("plan_id", planRow.id)
        .eq("user_id", userId)
        .maybeSingle();
      followRow = data || null;
    }
    setFollow(followRow);
    setLoading(false);
  }, [churchId, userId]);

  useEffect(() => { load(); }, [load]);

  // No published plan — fall back to the latest sermon on its own, so This
  // Week isn't just an empty message (Sermon-only state, docs/designs/WeekSermon).
  useEffect(() => {
    if (loading || plan || !churchId) { setStandaloneSermon(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("sermons")
        .select("id, title, display_title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)")
        .eq("church_id", churchId)
        .order("date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setStandaloneSermon(data || null);
    })();
    return () => { cancelled = true; };
  }, [loading, plan, churchId]);

  // Any-status reading_plans row for the standalone sermon — not just
  // published (that's what `plan` already covers) — so the "For leaders"
  // banner doesn't re-offer generation when a draft is already mid-review.
  // Defaults to true (assume a plan exists) so the banner never flashes on
  // first render, only once we actually know there's nothing yet.
  const [standaloneSermonHasPlan, setStandaloneSermonHasPlan] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!standaloneSermon?.id) { setStandaloneSermonHasPlan(true); return; }
    supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("sermon_id", standaloneSermon.id)
      .then(({ count }) => { if (!cancelled) setStandaloneSermonHasPlan((count || 0) > 0); });
    return () => { cancelled = true; };
  }, [standaloneSermon?.id]);

  // "This week's verse" — same week's sermon regardless of plan state (see
  // weekSermon below), so it keeps showing once a plan is published, not
  // just in the no-plan fallback. Verse text fetched the same lightweight
  // way BibleApp.jsx's fetchSingleVerseText does (public-domain fallback,
  // no auth needed) — this is the only other call site, so inlined rather
  // than shared.
  const [weekVerseText, setWeekVerseText] = useState("");
  useEffect(() => {
    const ref = mainRefOf(plan?.sermons || standaloneSermon);
    if (!ref) { setWeekVerseText(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const bookApi = (ref.book || "").toLowerCase().replace(/\s+/g, "+");
        const r = await fetch(`https://bible-api.com/${bookApi}+${ref.chapter}:${ref.verse}?translation=web`);
        if (!r.ok) { if (!cancelled) setWeekVerseText(""); return; }
        const d = await r.json();
        const t = String(d.text || d.verses?.[0]?.text || "").replace(/\s+/g, " ").trim();
        // A complete verse, never a fragment or cross-reference — a cheap
        // proxy is that it starts with a capital letter (docs/FIX-SERMON-CLIPS.md §3c).
        if (!cancelled) setWeekVerseText(/^[A-Z]/.test(t) ? t : "");
      } catch { if (!cancelled) setWeekVerseText(""); }
    })();
    return () => { cancelled = true; };
  }, [plan?.sermons, standaloneSermon]);

  // "Create it" — fetch the full sermon row (standaloneSermon deliberately
  // excludes `transcript`, which generateReadingPlan needs) and open the same
  // review sheet FullSermonSheet/PlansTab already use to generate+approve.
  const [creatingPlanTarget, setCreatingPlanTarget] = useState(null); // { sermon, sermonRefs } | null
  const [creatingPlanLoading, setCreatingPlanLoading] = useState(false);
  async function handleCreateIt() {
    if (!standaloneSermon?.id || creatingPlanLoading) return;
    setCreatingPlanLoading(true);
    const { data: fullSermon } = await supabase.from("sermons").select("*").eq("id", standaloneSermon.id).single();
    setCreatingPlanLoading(false);
    if (fullSermon) setCreatingPlanTarget({ sermon: fullSermon, sermonRefs: standaloneSermon.sermon_refs || [] });
  }
  function refreshAfterPlanSave() {
    setCreatingPlanTarget(null);
    load();
    if (standaloneSermon?.id) {
      supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("sermon_id", standaloneSermon.id)
        .then(({ count }) => setStandaloneSermonHasPlan((count || 0) > 0));
    }
  }

  const completedSet = useMemo(() => new Set(follow?.days_completed || []), [follow]);
  const allDone = completedSet.size >= 5;

  // The day to work on next — earliest not-yet-completed, never "missed".
  // There is no day-locking, no "behind" state and no red/dashed days
  // anywhere (docs/FIX-PLANS.md principles) — skipping a day just means
  // the next day is "ready when you are".
  function earliestUnfinishedDay() {
    for (let n = 1; n <= 5; n++) if (!completedSet.has(n)) return n;
    return 1;
  }
  const nextDay = earliestUnfinishedDay();

  // Who's joined (not just who did today) — docs/designs/PlanOpt2a.dc.html /
  // PlanOpt2b.dc.html's "23 from New Life joined" / "You + 23 others". A
  // SECURITY DEFINER RPC, not a direct table read, since reading_plan_follows
  // is deliberately own-row-only RLS.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!plan?.id) { setTotalJoined({ profiles: [], total: 0 }); return; }
      const { data, error } = await supabase.rpc("reading_plan_total_joined", { p_plan_id: plan.id, p_limit: 3 });
      if (cancelled) return;
      if (error) { console.warn("reading_plan_total_joined:", error.message); setTotalJoined({ profiles: [], total: 0 }); return; }
      setTotalJoined({ profiles: data || [], total: (data && data[0]?.total_count) || 0 });
    }
    run();
    return () => { cancelled = true; };
  }, [plan?.id]);


  async function startPlan() {
    if (!userId || !plan || follow || starting) return;
    setStarting(true);
    const { data } = await supabase
      .from("reading_plan_follows")
      .upsert({ plan_id: plan.id, user_id: userId, last_opened_day: 1 }, { onConflict: "plan_id,user_id" })
      .select().single();
    setFollow(data);
    setStarting(false);
    onOpenPlanDay?.(plan, data, 1);
  }

  // ── "No church yet" find-your-church search (redesign Phase 8a) ──────
  const [noChurchSearch, setNoChurchSearch] = useState("");
  const [noChurchResults, setNoChurchResults] = useState([]);
  const [joiningChurchId, setJoiningChurchId] = useState(null);
  const [requestedChurchIds, setRequestedChurchIds] = useState(() => new Set());

  useEffect(() => {
    if (churchId) return;
    const q = noChurchSearch.trim();
    if (!q) { setNoChurchResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase.from("churches")
        .select("id, name, location, invite_only")
        .or(`name.ilike.%${q}%,location.ilike.%${q}%`)
        .limit(8);
      if (!cancelled) setNoChurchResults(data || []);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [noChurchSearch, churchId]);

  async function joinChurch(c) {
    if (!userId || joiningChurchId) return;
    setJoiningChurchId(c.id);
    await supabase.from("profiles").update({ church_id: c.id }).eq("id", userId);
    await supabase.from("user_church_follows").upsert({ user_id: userId, church_id: c.id }, { onConflict: "user_id,church_id" });
    await refreshProfile?.();
    setJoiningChurchId(null);
  }
  async function requestToJoinChurch(c) {
    if (requestedChurchIds.has(c.id)) return;
    setRequestedChurchIds(prev => new Set(prev).add(c.id));
    await requestAccess?.("membership", c.id);
  }

  const totalJoinedProfilesById = useMemo(() => {
    const m = {};
    for (const p of totalJoined.profiles) m[p.user_id] = { full_name: p.full_name, avatar_url: p.avatar_url };
    return m;
  }, [totalJoined.profiles]);

  if (loading) {
    return <div style={S.wrap}><div style={S.empty}>Loading…</div></div>;
  }

  if (!churchId) {
    return (
      <div style={S.wrap}>
        <div style={S.noChurchCard}>
          <div style={S.noChurchStep}>STEP 1</div>
          <div style={S.noChurchHeading}>Find your church</div>
          <div style={S.noChurchSub}>Get their Sunday sermons, weekly reading plans and community in one place.</div>
          <input
            style={S.noChurchSearchInput}
            placeholder="Church name or town"
            value={noChurchSearch}
            onChange={e => setNoChurchSearch(e.target.value)}
          />
          {noChurchResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {noChurchResults.map(c => {
                const requested = requestedChurchIds.has(c.id);
                const joining = joiningChurchId === c.id;
                return (
                  <div key={c.id} style={S.noChurchResultRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.noChurchResultName}>{c.name}</div>
                      {c.location && <div style={S.noChurchResultMeta}>{c.location}</div>}
                    </div>
                    {c.invite_only ? (
                      <button style={S.noChurchRequestBtn} disabled={requested} onClick={() => requestToJoinChurch(c)}>
                        {requested ? "Requested ✓" : "Request"}
                      </button>
                    ) : (
                      <button style={S.noChurchJoinBtn} disabled={joining} onClick={() => joinChurch(c)}>
                        {joining ? "…" : "Join"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={S.whileLookSection}>
          <div style={S.sectionTitle}>While you look</div>
          <button style={S.johnCard} onClick={() => onOpenVerse?.("John", 1, 1)}>
            <div style={S.johnIcon}>📖</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.johnTitle}>Start with the Gospel of John</div>
              <div style={S.johnMeta}>A great first book · 21 short chapters</div>
            </div>
          </button>
        </div>

        <div style={S.cantFindCard}>
          <div style={S.cantFindHeading}>Can't see your church?</div>
          <button style={S.inviteLeaderRow} onClick={() => shareChurchInvite()}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.inviteLeaderTitle}>Invite your church leader</div>
              <div style={S.inviteLeaderMeta}>Send them a ready-made message</div>
            </div>
            <span aria-hidden="true" style={{ fontWeight: 800 }}>→</span>
          </button>
        </div>
      </div>
    );
  }

  const sermon = plan?.sermons;
  const leaderTotal = leaderCounts.reports + leaderCounts.plans + leaderCounts.access;
  const lastPlace = readLastPlace();

  // One sermon source regardless of plan state (docs/designs/ThisWeekStates.png:
  // the page layout is identical across every state) — the published plan's
  // sermon when there is one, else the latest standalone sermon.
  const weekSermon = sermon || standaloneSermon;
  // One canonical main-passage ref, shared by the sermon card's meta line and
  // "This week's verse" — never two different Bible references on the page
  // (docs/FIX-SERMON-CLIPS.md §3c).
  const weekRef = mainRefOf(weekSermon);
  const thumbBase = weekSermon?.youtube_id ? `https://i.ytimg.com/vi/${weekSermon.youtube_id}` : null;
  // Elder viewing a sermon with no plan yet AND nothing already mid-review
  // (a draft/approved-but-unpublished plan existing shouldn't re-offer
  // "Create it" — standaloneSermonHasPlan covers any status, not just published).
  const showCreateItStrip = isModerator && !plan && !!standaloneSermon && !standaloneSermonHasPlan;

  return (
    <div style={S.wrap}>
      <style>{"@keyframes tw-pulse{0%,100%{box-shadow:0 0 0 0 rgba(232,200,122,0)} 50%{box-shadow:0 0 0 10px rgba(232,200,122,0.35)}} .tw-pulse{animation:tw-pulse 1.6s ease-in-out 2}"}</style>

      {/* "For leaders" pill — independent of plan/join state (docs/designs/
          ThisWeekStates.png: it's only tied to whether there's something to
          act on, not to whether a plan exists). The missing-devotional
          prompt is NOT part of this — it's the sermon card's own strip now. */}
      {isModerator && leaderTotal > 0 && (
        <button type="button" style={S.leaderCard} onClick={onOpenLeaders}>
          <span style={S.leaderPillEyebrow}>For leaders</span>
          <span style={S.leaderPillSummary}>· {leaderSummaryText(leaderCounts)}</span>
          <span style={S.leaderPillChevron}>›</span>
        </button>
      )}

      {/* Sunday's sermon + plan, ONE card (docs/designs/PlanOpt2a.dc.html /
          PlanOpt2b.dc.html): white sermon row on top, a strip along the
          bottom that varies by state — joined / not joined / all done /
          "no plan yet" (elders) — or no strip at all (members, no plan). */}
      {weekSermon && (
        <>
          <div style={S.mergedCard} className={pulseDevo ? "tw-pulse" : undefined}>
            <div style={S.sermonRow} onClick={() => onOpenSermon?.(weekSermon)}>
              <div style={S.sermonThumbWrap}>
                {thumbBase ? (
                  <img
                    src={`${thumbBase}/maxresdefault.jpg`}
                    alt=""
                    style={S.sermonThumb}
                    onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = `${thumbBase}/hqdefault.jpg`; }}
                  />
                ) : <div style={{ ...S.sermonThumb, background: "var(--border)" }}/>}
                <div style={S.playDot}>▶</div>
                {weekSermon.duration && <div style={S.durationBadge}>{weekSermon.duration}</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.sermonEyebrow}>Sunday at {churchName || "your church"}</div>
                <div style={S.sermonTitle}>{displayTitleOf(weekSermon)}</div>
                {weekRef && <div style={S.sermonMeta}>{rangeLabelOf(weekRef)} · Recap →</div>}
              </div>
            </div>

            {plan && plan.reading_plan_days?.length > 0 ? (
              <div style={S.planStrip}>
                <div style={S.planStripEyebrow}>THIS WEEK'S BIBLE PLAN <span style={S.planStripEyebrowSub}>· from Sunday's sermon</span></div>
                {allDone ? (
                  <div style={S.stripBottomRow}>
                    <div style={S.stripJoinedLine}>All 5 days done ✓</div>
                    <button style={S.stripBtn} onClick={() => onOpenPlanDay?.(plan, follow, 5)}>Comments</button>
                  </div>
                ) : follow ? (() => {
                  const day = plan.reading_plan_days.find(d => d.day_number === nextDay);
                  const dayTitle = day?.title;
                  const dayPassage = day?.passage_ref;
                  return (
                    <>
                      <div style={S.dayCountRow}>
                        <span style={S.dayCountLabel}>Day {nextDay} of 5</span>
                        {completedSet.size > 0 && <span style={S.dayCountDone}>{completedSet.size} done</span>}
                      </div>
                      <div style={S.progressBar}>
                        {[1, 2, 3, 4, 5].map(n => (
                          <div key={n} style={{
                            ...S.progressSegment,
                            ...(completedSet.has(n) ? S.progressSegmentDone : n === nextDay ? S.progressSegmentCurrent : S.progressSegmentFuture),
                          }}/>
                        ))}
                      </div>
                      <div style={S.stripBottomRow}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {/* The passage must never appear on both lines — line 1 is the
                              title, falling back to the passage only when there's no
                              title, in which case line 2 drops the passage. */}
                          <div style={S.stripDayTitle}>{dayTitle || dayPassage || ""}</div>
                          <div style={S.stripDayMeta}>{dayTitle ? (dayPassage ? `${dayPassage} · 5 min` : "5 min") : "5 min"}</div>
                          <div style={S.stripJoinedRow}>
                            <FaceStack userIds={totalJoined.profiles.map(p => p.user_id)} profilesById={totalJoinedProfilesById} size={24} ringColor="#E8C87A"/>
                            <span style={S.stripJoinedLine}>
                              {totalJoined.total <= 1 ? "Just you so far" : `You + ${totalJoined.total - 1} others`}
                            </span>
                          </div>
                        </div>
                        <button style={S.stripBtn} onClick={() => onOpenPlanDay?.(plan, follow, nextDay)}>
                          {completedSet.size > 0 ? "Continue" : "Start"}
                        </button>
                      </div>
                    </>
                  );
                })() : (
                  <div style={S.stripBottomRow}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={S.stripDayTitle}>5-day plan from this sermon</div>
                      {totalJoined.total > 0 && (
                        <div style={S.stripJoinedRow}>
                          <FaceStack userIds={totalJoined.profiles.map(p => p.user_id)} profilesById={totalJoinedProfilesById} size={24} ringColor="#E8C87A"/>
                          <span style={S.stripJoinedLine}>{totalJoined.total} from {churchName || "your church"} joined</span>
                        </div>
                      )}
                    </div>
                    <button style={S.stripBtn} disabled={starting} onClick={startPlan}>{starting ? "…" : "Join"}</button>
                  </div>
                )}
              </div>
            ) : showCreateItStrip ? (
              <div style={S.planStrip}>
                <div style={S.stripBottomRow}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={S.leaderTitle}>For leaders</div>
                    <div style={S.noDevoText}>No plan for this sermon yet</div>
                    <div style={S.stripJoinedLine}>Members see it once you publish</div>
                  </div>
                  <button type="button" style={S.createItBtn} disabled={creatingPlanLoading} onClick={handleCreateIt}>
                    {creatingPlanLoading ? "…" : "Create it"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* No plan, member view: a separate "Read the passage" row below
              the card (not a strip — there's nothing to join yet). */}
          {!plan && !isModerator && weekRef && (
            <button type="button" style={S.readPassageRow} onClick={() => onOpenBible?.(weekRef.book, weekRef.chapter)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.stripDayTitle}>Read the passage</div>
                <div style={S.noDevoText}>No reading plan this week</div>
              </div>
              <div style={S.readPassagePill}>{weekRef.book} {weekRef.chapter} →</div>
            </button>
          )}
        </>
      )}

      {/* This week's verse — always shown when there's a complete verse,
          regardless of plan/join state (docs/designs/ThisWeekStates.png) —
          but hidden entirely if weekVerseText failed its validity check
          (fragment/cross-reference), not just left blank. */}
      {weekRef && weekVerseText && (
        <div style={S.verseCard}>
          <div style={S.verseCardHead}>
            <div style={S.verseCardLabel}>This week's verse</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" aria-label="Save verse" style={S.verseIconBtn}
                onClick={() => onSaveVerse?.(weekRef.book, weekRef.chapter, weekRef.verse, weekVerseText)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"/></svg>
              </button>
              <button type="button" aria-label="Share verse" style={S.verseIconBtn}
                onClick={() => shareTextViaBestChannel(`"${weekVerseText}" — ${weekRef.verse_ref || `${weekRef.book} ${weekRef.chapter}:${weekRef.verse}`}`)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>
              </button>
            </div>
          </div>
          <div style={S.verseCardText}>“{weekVerseText}”</div>
          <div style={S.verseCardRef}>{weekRef.verse_ref || `${weekRef.book} ${weekRef.chapter}:${weekRef.verse}`}</div>
        </div>
      )}

      <div style={S.eventsSection}>
        <div style={S.eventsSectionHead}>
          <div style={S.sectionTitle}>This week's events</div>
          {isModerator && (
            <button type="button" aria-label="Manage events" aria-pressed={manageEventsOpen}
              style={{ ...S.cogBtn, ...(manageEventsOpen ? S.cogBtnOn : null) }}
              onClick={() => setManageEventsOpen(o => !o)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          )}
        </div>
        {manageEventsOpen && isModerator && (
          <button type="button" style={S.addEventBtn} onClick={() => onAddEvent?.()}>+ Add event</button>
        )}
        {eventsError && (
          <div style={S.eventsErrorCard}>Couldn't load events: {eventsError}</div>
        )}
        {!eventsError && events.length === 0 && (
          <div style={S.noEventsCard}>
            <div style={S.noEventsIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--stone)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="3"/><path d="M16 2v4M8 2v4M3 10h18" stroke="#C8372A"/></svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={S.noEventsTitle}>Nothing on this week</div>
              <div style={S.noEventsSub}>Add your Sunday service, groups or prayer meetings. Repeating events are fine.</div>
            </div>
          </div>
        )}
        {events.map(ev => (
            <div key={`${ev.id}-${ev.occurrenceDate}`} style={S.eventRow}>
              <div style={{ ...S.eventBadge, background: EVENT_BADGE.bg, color: EVENT_BADGE.ink }}>
                {fmtEventBadge(ev.occursAt)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.eventTitle}>{ev.title}</div>
                <div style={S.eventMeta}>{[ev.time, ev.location].filter(Boolean).join(" · ")}</div>
                {ev.goingCount > 0 && (
                  <div style={S.faceRow}>
                    <FaceStack userIds={ev.goingIds} profilesById={ev.goingProfilesById} size={20}/>
                    <span style={S.faceCount}>{ev.going ? `You + ${ev.goingCount - 1} going` : `${ev.goingCount} going`}</span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                <button
                  style={{ ...S.goingBtn, ...(ev.going ? S.goingOn : null) }}
                  onClick={() => handleToggleGoing(ev)}
                >
                  {ev.going ? "Going ✓" : "I'm going"}
                </button>
                {rsvpError?.key === `${ev.id}-${ev.occurrenceDate}` && (
                  <div style={S.rsvpErrorText}>{rsvpError.message}</div>
                )}
                {isModerator && manageEventsOpen && (
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      type="button"
                      style={S.editEventBtn}
                      onClick={() => onEditEvent?.({
                        id: ev.id,
                        name: ev.title,
                        event_date: ev.occurrenceDate,
                        event_time: ev.time,
                        location: ev.location,
                        description: ev.description,
                        recurrence: ev.recurring,
                        repeat_until: ev.repeatUntil,
                      }, ev.occurrenceDate)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      style={S.deleteEventBtn}
                      onClick={() => onDeleteEvent?.({
                        id: ev.id,
                        name: ev.title,
                        event_date: ev.occurrenceDate,
                        event_time: ev.time,
                        location: ev.location,
                        description: ev.description,
                        recurrence: ev.recurring,
                        repeat_until: ev.repeatUntil,
                      }, ev.occurrenceDate)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Keep reading / Pause & pray — always last, regardless of plan state.
          Equal 1fr/1fr tiles, same vertical-stack layout (docs/designs/ThisWeekFixed.png):
          icon tile on top, bold title, grey subtitle below. */}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button type="button" style={S.keepReadingCard} onClick={() => onOpenBible?.()}>
          <div style={S.tileIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sky-ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15z"/></svg>
          </div>
          <div style={S.keepReadingTitle}>Keep reading</div>
          <div style={S.keepReadingSub}>{lastPlace.book} {lastPlace.chap} · where you left off</div>
        </button>
        <button type="button" style={S.prayCard} onClick={() => onOpenCommunity?.()}>
          <div style={{ ...S.tileIcon, background: "var(--white)" }}>🙏</div>
          <div style={S.prayTitle}>Pause &amp; pray</div>
          <div style={S.praySub}>See what your church is praying for</div>
        </button>
      </div>

      {creatingPlanTarget && (
        <ReadingPlanReviewSheet
          sermon={creatingPlanTarget.sermon}
          sermonRefs={creatingPlanTarget.sermonRefs}
          existingPlan={null}
          userId={userId}
          onClose={() => setCreatingPlanTarget(null)}
          onSaved={refreshAfterPlanSave}
        />
      )}
    </div>
  );
}

const S = {
  wrap: { padding: "16px 22px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, overflowX: "hidden" },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },

  noChurchCard: { borderRadius: 28, background: "var(--peach)", padding: 20, display: "flex", flexDirection: "column", gap: 12 },
  noChurchStep: { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--peach-ink)", opacity: 0.75 },
  noChurchHeading: { fontSize: 23, fontWeight: 800, letterSpacing: "-0.01em", color: "var(--peach-ink)", maxWidth: 240, lineHeight: 1.2 },
  noChurchSub: { fontSize: 13.5, lineHeight: 1.45, maxWidth: 240, color: "var(--peach-ink)", opacity: 0.85 },
  noChurchSearchInput: { background: "var(--white)", border: "none", borderRadius: 999, padding: "12px 16px", fontSize: 14.5, color: "var(--ink)", fontFamily: SANS, outline: "none", boxSizing: "border-box" },
  noChurchResultRow: { display: "flex", alignItems: "center", gap: 10, background: "var(--white)", borderRadius: 16, padding: "10px 12px" },
  noChurchResultName: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  noChurchResultMeta: { fontSize: 12, color: "var(--stone)", marginTop: 1 },
  noChurchJoinBtn: { flexShrink: 0, background: "var(--ink)", color: "var(--white)", border: "none", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
  noChurchRequestBtn: { flexShrink: 0, background: "transparent", border: "1px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },

  whileLookSection: { marginTop: 26 },
  johnCard: { width: "100%", display: "flex", gap: 14, alignItems: "center", background: "var(--sky)", border: "none", borderRadius: 24, padding: 14, cursor: "pointer", textAlign: "left", fontFamily: SANS },
  johnIcon: { flexShrink: 0, width: 50, height: 50, borderRadius: 16, background: "var(--white)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 },
  johnTitle: { fontSize: 16, fontWeight: 700, color: "var(--sky-ink)" },
  johnMeta: { fontSize: 12.5, color: "var(--sky-ink)", opacity: 0.75, marginTop: 2 },

  cantFindCard: { marginTop: 22, borderRadius: 22, border: "2px dashed var(--border-strong)", padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  cantFindHeading: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)" },
  inviteLeaderRow: { display: "flex", gap: 12, alignItems: "center", background: "var(--sage)", color: "var(--sage-ink)", border: "none", borderRadius: 16, padding: "10px 12px", cursor: "pointer", textAlign: "left", fontFamily: SANS },
  inviteLeaderTitle: { fontSize: 14, fontWeight: 700 },
  inviteLeaderMeta: { fontSize: 12, opacity: 0.85, marginTop: 1 },

  // One dark pill, single line (docs/designs/ThisWeekFixed.png) — replaces
  // the old white bordered card.
  leaderCard: { width: "100%", display: "flex", alignItems: "center", gap: 6, background: "#1F1B16", border: "none", borderRadius: 999, padding: "14px 18px", marginBottom: 14, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  leaderPillEyebrow: { flexShrink: 0, fontSize: 12, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: GOLD },
  leaderPillSummary: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  leaderPillChevron: { flexShrink: 0, fontSize: 16, color: "rgba(255,255,255,0.6)" },
  // Still used inside the "no plan yet" gold strip below (ink text on gold).
  leaderTitle: { fontSize: 14, fontWeight: 800, color: "var(--ink)" },

  mergedCard: { background: "var(--white)", borderRadius: 24, overflow: "hidden" },
  sermonRow: { display: "flex", gap: 12, alignItems: "center", padding: 12, cursor: "pointer" },
  sermonThumbWrap: { position: "relative", flexShrink: 0, width: 104, height: 58, borderRadius: 10, overflow: "hidden", background: "var(--border)" },
  sermonThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  playDot: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, textShadow: "0 1px 4px rgba(0,0,0,0.5)" },
  durationBadge: { position: "absolute", right: 6, bottom: 6, background: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 9.5, fontWeight: 700, borderRadius: 5, padding: "1px 5px" },
  sermonEyebrow: { fontSize: 11.5, fontWeight: 700, color: "var(--lavender-ink)", marginBottom: 2 },
  sermonTitle: { fontSize: 16, fontWeight: 800, color: "var(--ink)", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  sermonMeta: { fontSize: 12, color: "var(--stone)", marginTop: 3 },

  // Gold strip along the bottom of the merged card (docs/designs/PlanOpt2a.dc.html
  // / PlanOpt2b.dc.html) — not joined: "5-day plan…" + faces + Join; joined:
  // day pills + "Day n · title" + faces + Start/Continue; all done: ticked
  // pills + "All 5 days done" + Comments.
  planStrip: { background: GOLD, padding: "14px 14px", display: "flex", flexDirection: "column", gap: 10 },
  planStripEyebrow: { fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", color: "#3A2E10" },
  planStripEyebrowSub: { fontWeight: 600, opacity: 0.8, textTransform: "none" },
  // Progress bar (docs/designs/PlanStripOptions.png option C) replaces the
  // old numbered day pills — says "Day N of 5" directly rather than making
  // the member work out what the numbers mean, and never reads as "behind"
  // no matter which day they start on.
  dayCountRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between" },
  dayCountLabel: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  dayCountDone: { fontSize: 12.5, fontWeight: 700, color: "#3A2E10" },
  progressBar: { display: "flex", gap: 4 },
  progressSegment: { flex: 1, height: 8, borderRadius: 4 },
  progressSegmentDone: { background: "var(--ink)" },
  progressSegmentCurrent: { background: "var(--white)", boxShadow: "0 0 0 2px #7C6BC4" },
  progressSegmentFuture: { background: "var(--cream)" },
  stripBottomRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  stripDayTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  stripDayMeta: { fontSize: 12.5, color: "#5C4A1E", marginTop: 1 },
  stripJoinedRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 4 },
  stripJoinedLine: { fontSize: 12.5, fontWeight: 700, color: "#3A2E10" },
  stripBtn: { flexShrink: 0, background: "#1F1B16", color: "#fff", border: "none", borderRadius: 999, padding: "10px 18px", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: SANS },

  faceRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
  faceCount: { fontSize: 12, color: "var(--ink)", opacity: 0.75 },

  noDevoText: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)", marginTop: 2 },
  createItBtn: { flexShrink: 0, background: "#1F1B16", color: "#fff", border: "none", borderRadius: 999, padding: "9px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },

  // Keep reading / Pause & pray — same vertical-stack structure, equal
  // 1fr/1fr width (docs/designs/ThisWeekFixed.png). Both use `tileIcon` below,
  // just a different background per side.
  tileIcon: { flexShrink: 0, width: 38, height: 38, borderRadius: 13, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 },
  keepReadingCard: { flex: 1, borderRadius: 22, background: "var(--sky)", color: "var(--sky-ink)", padding: 14, display: "flex", flexDirection: "column", border: "none", cursor: "pointer", textAlign: "left", fontFamily: SANS, boxSizing: "border-box" },
  keepReadingTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  keepReadingSub: { fontSize: 12.5, color: "var(--sky-ink)", opacity: 0.85, marginTop: 2, lineHeight: 1.35 },

  prayCard: { flex: 1, borderRadius: 22, background: "var(--blush)", color: "var(--blush-ink)", padding: 14, border: "none", cursor: "pointer", textAlign: "left", fontFamily: SANS, display: "flex", flexDirection: "column", boxSizing: "border-box" },
  prayTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  praySub: { fontSize: 12.5, opacity: 0.85, lineHeight: 1.35, marginTop: 2 },
  readPassageRow: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "none", borderRadius: 18, padding: "14px 16px", marginTop: 10, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  readPassagePill: { flexShrink: 0, border: "1.5px solid var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 800, color: "var(--ink)" },

  verseCard: { background: "var(--white)", borderRadius: 22, padding: 16, marginTop: 10, display: "flex", flexDirection: "column", gap: 8 },
  verseCardHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  verseCardLabel: { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)" },
  verseIconBtn: { width: 32, height: 32, borderRadius: 16, border: "none", background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  verseCardText: { fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 18, lineHeight: 1.5, color: "var(--ink)" },
  verseCardRef: { fontSize: 13, fontWeight: 800, color: "var(--stone)" },

  eventsSection: { marginTop: 26 },
  eventsSectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 },
  noEventsCard: { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--white)", borderRadius: 20, padding: 14 },
  eventsErrorCard: { background: "rgba(249,112,102,0.10)", border: "1px solid rgba(249,112,102,0.30)", borderRadius: 14, padding: "12px 14px", fontSize: 12.5, color: "#C8372A", fontFamily: SANS },
  rsvpErrorText: { fontSize: 10.5, color: "#C8372A", maxWidth: 110, textAlign: "right", lineHeight: 1.3 },
  noEventsIcon: { flexShrink: 0, width: 44, height: 44, borderRadius: 14, background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center" },
  noEventsTitle: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)" },
  noEventsSub: { fontSize: 12.5, color: "var(--stone)", lineHeight: 1.4, marginTop: 2 },
  sectionTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--gold-ink)" },
  addEventBtn: { flexShrink: 0, background: "transparent", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, marginBottom: 10 },
  cogBtn: { flexShrink: 0, width: 28, height: 28, borderRadius: 14, background: "transparent", border: "none", color: "var(--stone)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  cogBtnOn: { background: "var(--gold)", color: "var(--ink)" },
  eventRow: { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: 12, marginBottom: 10 },
  eventBadge: { flexShrink: 0, fontSize: 11, fontWeight: 800, borderRadius: 10, padding: "6px 9px", textAlign: "center", lineHeight: 1.2 },
  eventTitle: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)" },
  eventMeta: { fontSize: 12, color: "var(--stone)", marginTop: 2, marginBottom: 6 },
  goingBtn: { flexShrink: 0, background: "transparent", border: "1px solid var(--border-strong)", color: "var(--stone)", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, whiteSpace: "nowrap" },
  goingOn: { background: "var(--ink)", color: "var(--white)", border: "none" },
  editEventBtn: { flexShrink: 0, background: "none", border: "none", color: "var(--stone-dim)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "2px 2px" },
  deleteEventBtn: { flexShrink: 0, background: "none", border: "none", color: "#C8372A", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "2px 2px" },
};
